import { useEffect, useRef, useState } from "react";
import { BookerContextValues } from "./BookerContext";
import {
  EnumOwnerType,
  useBookerByIdQuery,
  useOperatorByIdLazyQuery,
  useStorefrontByIdLazyQuery,
} from "gql/generated";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";

export const useGetBookerData = (
  bookerId: string,
  hasOpened: boolean,
  isEmbedded: boolean,
  bookerModalOpen: boolean,
) => {
  const [booker, setBooker] = useState<BookerContextValues["booker"]>(null);
  const [product, setProduct] = useState<BookerContextValues["product"]>(null);
  const [owner, setOwner] = useState<BookerContextValues["owner"]>(null);
  const [stripeTestMode, setStripeTestMode] = useState(false);
  const [storefrontIsPaired, setStorefrontIsPaired] = useState(false);

  const [getOperator, { loading: operatorLoading, error: operatorError }] =
    useOperatorByIdLazyQuery();
  const [getStorefront] = useStorefrontByIdLazyQuery();

  const trackPostHogEvent = useWidgetTrack();

  const { loading: bookerLoading, error: bookerError, refetch } =
    useBookerByIdQuery({
    variables: { id: bookerId },
    skip: !bookerId,
    onCompleted: async data => {
      if (data.bookerById) {
        setBooker(data.bookerById as BookerContextValues["booker"]);

        if (data.bookerById.storefront) {
          try {
            const sfResult = await getStorefront({
              variables: { id: data.bookerById.storefront },
            });
            setStripeTestMode(!!sfResult.data?.storefrontById?.testMode);
            setStorefrontIsPaired(!!sfResult.data?.storefrontById?.isPaired);
          } catch (e) {
            console.warn("Failed to load storefront data", e);
          }
        }

        const mainProduct = data.bookerById.bundles[0]?.product;

        if (mainProduct) {
          setProduct(mainProduct as BookerContextValues["product"]);
        }

        const mainProductOwner =
          mainProduct?.owner.type === EnumOwnerType.Operator
            ? await getOperator({
                variables: { operatorId: mainProduct.owner.entityId },
              })
            : null; // TODO: Need to handle cases where the owner type is an ORGANIZATION

        if (mainProductOwner) {
          setOwner(
            mainProductOwner.data?.operatorById as unknown as BookerContextValues["owner"],
          );
        }
      }
    },
  });

  const wasModalOpen = useRef(false);
  useEffect(() => {
    if (!bookerId) return;
    if (bookerModalOpen && !wasModalOpen.current) {
      void refetch();
    }
    wasModalOpen.current = bookerModalOpen;
  }, [bookerId, bookerModalOpen, refetch]);

  useEffect(() => {
    if (hasOpened && isEmbedded && booker) {
      // When the modal is opened & the booker is embedded, track the booker loaded event
      trackPostHogEvent(PostHogCustomEvent.BookerLoaded, {
        bookerId,
      });
    }
  }, [hasOpened, isEmbedded, trackPostHogEvent, bookerId, booker]);

  return {
    booker,
    product,
    owner,
    stripeTestMode,
    storefrontIsPaired,
    loading: bookerLoading || operatorLoading,
    error: !!bookerError || !!operatorError,
  };
};
